import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import { toast } from "react-toastify";
import { OLIVEEB_APP_OPTIONS } from "constants";

export default function CreateOrUpdatePrivilegeModal({
  show,
  onHide,
  onSave,
  privilege,
}) {
  const isUpdate = Boolean(privilege);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => ({
      name: privilege?.name || "",
      owner: privilege?.owner || "",
      type: privilege?.type || "SYSTEM",
      scope: privilege?.scope || "",
      description: privilege?.description || "",
    }),
    [privilege]
  );

  useEffect(() => {
    if (show) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [form, initialValues, show]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      await onSave(
        {
          name: String(values.name || "").trim(),
          owner: String(values.owner || "").trim(),
          type: String(values.type || "SYSTEM").trim(),
          scope: String(values.scope || "").trim(),
          description: String(values.description || "").trim(),
        },
        privilege?.id
      );

      onHide();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.message || "Failed to save privilege");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={show}
      onCancel={onHide}
      centered
      destroyOnClose
      zIndex={1300}
      title={isUpdate ? "Update Privilege" : "Add Privilege"}
      footer={
        <Space className="w-100 justify-content-end">
          <Button onClick={onHide}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            {isUpdate ? "Update" : "Save"}
          </Button>
        </Space>
      }
      styles={{
        body: { paddingTop: 8 },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        requiredMark={false}
      >
        <Form.Item
          label="Privilege Name"
          name="name"
          rules={[{ required: true, message: "Privilege name is required" }]}
        >
          <Input placeholder="Enter privilege name" autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Owner"
          name="owner"
          rules={[{ required: true, message: "Owner app is required" }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select owner app"
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children || "")
                .toLowerCase()
                .includes(String(input || "").toLowerCase())
            }
          >
            {OLIVEEB_APP_OPTIONS.map((opt) => (
              <Select.Option key={opt.label} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Scope"
          name="scope"
          rules={[{ required: true, message: "Scope is required" }]}
        >
          <Input placeholder="Enter scope or permission key" autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Privilege type is required" }]}
          extra="Use Product for privileges owned by a product service, such as Oliveeb Care."
        >
          <Select placeholder="Select privilege type">
            <Select.Option value="SYSTEM">System</Select.Option>
            <Select.Option value="PRODUCT">Product</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Short description for this privilege" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
